import { useEffect, useState } from "react";
import client from "../api/client";
import { useMasterPins } from "../context/PinContext";
import { logEmployeeActivity } from "../utils/activityLogger";
import { MASTER_PIN_ACTIONS } from "../utils/masterPinActions";

const normalizeEmployees = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const getEmployeeName = (employee) =>
  employee?.name ||
  `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() ||
  `Employee #${employee?.id || "Unknown"}`;

const getEmployeeKey = (employee) =>
  String(employee?.id ?? employee?.employee_id ?? employee?.email ?? getEmployeeName(employee));

export default function MasterPinDashboard() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPages, setSelectedPages] = useState({});
  const [reloadCount, setReloadCount] = useState(0);
  const [generatingKey, setGeneratingKey] = useState("");
  const { generatePin, getPin } = useMasterPins();

  useEffect(() => {
    let isMounted = true;

    const fetchEmployees = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await client.get("/users");
        if (!isMounted) {
          return;
        }

        const nextEmployees = normalizeEmployees(response.data);
        setEmployees(nextEmployees);
        setSelectedPages((previous) => {
          const updated = { ...previous };
          nextEmployees.forEach((employee) => {
            const employeeKey = getEmployeeKey(employee);
            if (!updated[employeeKey]) {
              updated[employeeKey] = MASTER_PIN_ACTIONS[0].key;
            }
          });
          return updated;
        });
      } catch (fetchError) {
        if (isMounted) {
          console.error("Failed to load employees for Master PIN dashboard:", fetchError);
          setError("Unable to load employees right now.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchEmployees();

    return () => {
      isMounted = false;
    };
  }, [reloadCount]);

  const handlePageChange = (employeeId, actionKey) => {
    setSelectedPages((previous) => ({
      ...previous,
      [employeeId]: actionKey,
    }));
  };

  const handleGeneratePin = async (employeeId) => {
    const selectedPage = selectedPages[employeeId] || MASTER_PIN_ACTIONS[0].key;
    const requestKey = `${employeeId}:${selectedPage}`;

    try {
      setGeneratingKey(requestKey);
      setError("");
      await generatePin(employeeId, selectedPage);
    } catch (generateError) {
      console.error("Failed to generate master PIN:", generateError);
      setError("Unable to generate PIN right now.");
    } finally {
      setGeneratingKey("");
    }
  };

  return (
    <div className="master-pin-dashboard">
      <header className="master-pin-header">
        <div>
          <div className="master-pin-badge-row">
            <span className="master-pin-eyebrow">Admin Tool</span>
            <span className="master-pin-admin-chip">Admin Only</span>
          </div>
          <h1>Master PIN Management</h1>
          <p>
            Generate temporary page-specific 4-digit PINs for employees without changing
            existing authentication or backend logic.
          </p>
        </div>
        <div className="master-pin-summary-card">
          <span>Employees</span>
          <strong>{employees.length}</strong>
          <small>Temporary PINs auto-hide after 30 seconds</small>
        </div>
      </header>

      <section className="master-pin-table-card">
        <div className="master-pin-table-header">
          <div>
            <h2>Employee Access PINs</h2>
            <p>Select a page, generate a PIN, and view it only for that employee row.</p>
          </div>
        </div>

        {loading ? (
          <div className="master-pin-state">Loading employees...</div>
        ) : error ? (
          <div className="master-pin-state is-error">
            <span>{error}</span>
            <button
              type="button"
              className="master-pin-secondary-button"
              onClick={() => setReloadCount((previous) => previous + 1)}
            >
              Retry
            </button>
          </div>
        ) : employees.length === 0 ? (
          <div className="master-pin-state">No employees available.</div>
        ) : (
          <div className="master-pin-table-wrap">
            <table className="master-pin-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Employee ID</th>
                  <th>Select Page</th>
                  <th>Generate PIN</th>
                  <th>Generated PIN</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => {
                  const employeeKey = getEmployeeKey(employee);
                  const employeeId = employee.id ?? employee.employee_id ?? "N/A";
                  const selectedPage = selectedPages[employeeKey] || MASTER_PIN_ACTIONS[0].key;
                  const visiblePin = getPin(employeeKey, selectedPage);

                  return (
                    <tr key={employeeKey}>
                      <td>
                        <div className="master-pin-employee-cell">
                          <strong>{getEmployeeName(employee)}</strong>
                          {employee.email ? <span>{employee.email}</span> : null}
                        </div>
                      </td>
                      <td>
                        <span className="master-pin-id-badge">
                          {employeeId === "N/A" ? "N/A" : `#${employeeId}`}
                        </span>
                      </td>
                      <td>
                        <select
                          className="master-pin-select"
                          value={selectedPage}
                          onChange={(event) =>
                            handlePageChange(employeeKey, event.target.value)
                          }
                        >
                          {MASTER_PIN_ACTIONS.map((option) => (
                            <option key={option.key} value={option.key}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="master-pin-primary-button master-pin-generate-button"
                          onClick={() => handleGeneratePin(employeeKey)}
                          disabled={generatingKey === `${employeeKey}:${selectedPage}`}
                        >
                          {generatingKey === `${employeeKey}:${selectedPage}`
                            ? "Generating..."
                            : "Generate PIN"}
                        </button>
                      </td>
                      <td>
                        {visiblePin ? (
                          <span className="master-pin-badge">
                            PIN: <strong>{visiblePin}</strong>
                          </span>
                        ) : (
                          <span className="master-pin-muted">Hidden until generated</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
